import type { ArticleSummary, ExperimentSummary, JsonLd, ProjectSummary } from '@/types'

import { siteConfig } from '@/constants/site'

/* schema.org JSON-LD builders. Rendered through <Seo jsonLd={...} />. */

const url = (path: string): string => `${siteConfig.url}${path}`

const publisher = {
    '@type': 'Organization',
    'name': siteConfig.name,
    'url': siteConfig.url,
    'logo': {
        '@type': 'ImageObject',
        'url': url('/favicon.svg')
    }
}

export const websiteJsonLd = (): JsonLd => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': siteConfig.name,
    'url': siteConfig.url,
    'description': siteConfig.description,
    'inLanguage': 'en',
    'publisher': publisher
})

export const breadcrumbJsonLd = (items: Array<{ name: string, path: string }>): JsonLd => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': items.map((item, index) => ({
        '@type': 'ListItem',
        'position': index + 1,
        'name': item.name,
        'item': url(item.path)
    }))
})

export const articleJsonLd = (article: ArticleSummary): JsonLd => ({
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    'headline': article.title,
    'description': article.excerpt,
    'datePublished': article.date,
    'dateModified': article.updatedAt ?? article.date,
    'url': url(`/journal/${article.slug}`),
    'mainEntityOfPage': url(`/journal/${article.slug}`),
    'articleSection': article.categoryLabel,
    'keywords': article.tags.join(', '),
    'inLanguage': 'en',
    'author': publisher,
    'publisher': publisher,
    'image': url(siteConfig.ogImage)
})

export const projectJsonLd = (project: ProjectSummary): JsonLd => ({
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    'name': project.title,
    'description': project.description,
    'dateCreated': project.date,
    'url': url(`/projects/${project.slug}`),
    'genre': project.categoryLabel,
    'keywords': project.technologies.join(', '),
    'creator': publisher,
    ...(project.sourceUrl ? { 'codeRepository': project.sourceUrl } : {})
})

export const experimentJsonLd = (experiment: ExperimentSummary): JsonLd => ({
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    'headline': experiment.title,
    'description': experiment.description,
    'datePublished': experiment.date,
    'dateModified': experiment.updatedAt ?? experiment.date,
    'url': url(`/lab/${experiment.slug}`),
    'about': experiment.categoryLabel,
    'keywords': experiment.technologies.join(', '),
    'author': publisher,
    'publisher': publisher
})
